import { useGesture } from '@hooks/useGesture'
import { useReadingSession } from '@hooks/useReadingSession'
import { useCallback, useEffect, useRef, useState } from 'react'
import useSWR from 'swr'
import { fetcher } from 'utils/fetcher'
import { TOTAL_QURAN_VERSES } from '@components/shared/consts'

interface UseVerseNavigationOptions {
  mode: 'daily' | 'standalone'
  initialVerse?: string | number
  challengeCurrentVerse?: number
}

export interface SwipeState {
  direction: 'left' | 'right' | null
  progress: number
  isActive: boolean
  isTriggered: boolean
}

const INITIAL_SWIPE_STATE: SwipeState = {
  direction: null,
  progress: 0,
  isActive: false,
  isTriggered: false,
}

export const useVerseNavigation = ({
  mode,
  initialVerse,
  challengeCurrentVerse,
}: UseVerseNavigationOptions) => {
  const { recordVerseRead } = useReadingSession()

  const [currentVerse, setCurrentVerse] = useState<string | number>(
    mode === 'standalone' ? (initialVerse || 1) : (challengeCurrentVerse || 1),
  )

  // Fetch current verse data
  const { data: verseData, isLoading } = useSWR(
    currentVerse ? `/api/gunluk-okuma?verse=${currentVerse}` : null,
    fetcher,
    { revalidateOnFocus: false },
  )

  // Sync current verse from challenge data
  useEffect(() => {
    if (mode === 'daily' && challengeCurrentVerse) {
      setCurrentVerse(challengeCurrentVerse)
    }
  }, [challengeCurrentVerse, mode])

  // Sync current verse when navigating to a different verse via URL (e.g. search)
  useEffect(() => {
    if (mode === 'standalone' && initialVerse) {
      setCurrentVerse(initialVerse)
    }
  }, [mode, initialVerse])

  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const getActiveVerseNum = useCallback((): number => {
    return verseData?.arabic?.number || (typeof currentVerse === 'number' ? currentVerse : 1)
  }, [verseData, currentVerse])

  const handleNext = useCallback(() => {
    const activeVerseNum = getActiveVerseNum()
    if (activeVerseNum >= TOTAL_QURAN_VERSES) return

    recordVerseRead()
    setCurrentVerse(activeVerseNum + 1)
  }, [getActiveVerseNum, recordVerseRead])

  const handlePrevious = useCallback(() => {
    const activeVerseNum = getActiveVerseNum()
    if (activeVerseNum <= 1) return
    setCurrentVerse(activeVerseNum - 1)
  }, [getActiveVerseNum])

  // Swipe gesture state
  const [swipeState, setSwipeState] = useState<SwipeState>(INITIAL_SWIPE_STATE)

  const handleSwipeEnd = useCallback(
    (direction: 'left' | 'right') => {
      setSwipeState((prev) => ({ ...prev, isTriggered: true, progress: 1 }))

      if (direction === 'left') {
        handlePrevious()
      } else {
        handleNext()
      }

      // Delay reset to allow "shoot-through" animation to complete
      setTimeout(() => {
        setSwipeState(INITIAL_SWIPE_STATE)
      }, 500)
    },
    [handleNext, handlePrevious],
  )

  useGesture({
    onSwipeLeft: () => handleSwipeEnd('left'),
    onSwipeRight: () => handleSwipeEnd('right'),
    onSwipeMove: (direction, progress) => {
      setSwipeState({ direction, progress, isActive: true, isTriggered: false })
    },
    onSwipeCancel: () => {
      setSwipeState(INITIAL_SWIPE_STATE)
    },
  })

  // Scroll to top when verse changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })

    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [currentVerse])

  // Track whether this is the first render or a popstate-triggered navigation
  const isInitialRender = useRef(true)
  const isPopstateNav = useRef(false)

  // Handle browser back/forward to navigate between verses
  useEffect(() => {
    const handlePopState = () => {
      const params = new URLSearchParams(window.location.search)
      const ayet = params.get('ayet')
      if (ayet) {
        isPopstateNav.current = true
        setCurrentVerse(Number(ayet))
      }
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  // Update URL and title when verse changes
  useEffect(() => {
    if (!verseData?.arabic?.number) return

    const basePath = mode === 'standalone' ? '/ayet' : '/gunluk-okuma'
    const newUrl = `${basePath}?ayet=${verseData.arabic.number}`
    const nextState = window.history.state
    const updatedState = { ...nextState, url: newUrl, as: newUrl }

    if (isInitialRender.current || isPopstateNav.current) {
      window.history.replaceState(updatedState, '', newUrl)
      isInitialRender.current = false
      isPopstateNav.current = false
    } else {
      window.history.pushState(updatedState, '', newUrl)
    }

    if (verseData.arabic.surah?.englishName && verseData.arabic.numberInSurah) {
      document.title = `${verseData.arabic.surah.englishName} Suresi ${verseData.arabic.numberInSurah}. Ayet`
    }
  }, [mode, verseData?.arabic?.number])

  const activeVerseNum = getActiveVerseNum()

  return {
    verseData,
    isLoading,
    activeVerseNum,
    handleNext,
    handlePrevious,
    swipeState,
    scrollContainerRef,
  }
}
